import { AxiosError } from 'axios';
import { FormikHelpers } from 'formik/dist/types';
import { useCallback } from 'react';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';

import { getRefreshToken } from '@app/redux/auth/auth.selectors';
import { getBranchEnvironment } from '@app/redux/branchEnvironment/branchEnvironment.selectors';
import { useAppSelector } from '@app/redux/hooks';
import { store } from '@app/redux/store';

import { VerifyEmailFormValues } from '@components/forms/verifyEmail/interfaces';

function useVerifyEmail({ onSuccess }: { onSuccess?: () => void }) {
	const { startRequest: verifyEmail } = useTriggeredRequest(AccountClientRequests.Users.verifyEmail);
	const { startRequest: handleRefreshToken } = useTriggeredRequest(AccountClientRequests.Auth.refreshToken);
	const branchEnvironment = useAppSelector(getBranchEnvironment);
	const refreshToken = useAppSelector(getRefreshToken);

	const initialValues: VerifyEmailFormValues = {
		code: '',
	};

	const onSubmit = useCallback(
		async (values: VerifyEmailFormValues, formikHelpers: FormikHelpers<VerifyEmailFormValues>) => {
			try {
				await verifyEmail({
					phone: store.getState().auth.currentUser?.phone || '',
					email_change_code: values.code,
					email: store.getState().auth.currentUser?.email || '',
					branch: store.getState().branchEnvironment.variables.branch_id,
					first_name: store.getState().auth.currentUser?.first_name || '',
					last_name: store.getState().auth.currentUser?.last_name || '',
					mfa_enabled: store.getState().auth.currentUser?.mfa_enabled || false,
				});

				await handleRefreshToken({ refresh_token: refreshToken, branch: branchEnvironment.branch_id });

				onSuccess?.();
			} catch (e) {
				const { response } = e as AxiosError<{
					messages: { code: string[] } | string[];
				}>;

				formikHelpers.setErrors({
					code: Array.isArray(response?.data?.messages)
						? response?.data?.messages?.join(' ') || 'Oops. Something went wrong.'
						: response?.data?.messages?.code?.join(' ') || 'Oops. Something went wrong.',
				});
			}
		},
		[verifyEmail, handleRefreshToken, refreshToken, branchEnvironment.branch_id, onSuccess],
	);

	return {
		initialValues,
		onSubmit,
	};
}

export default useVerifyEmail;
